<template>
  <div class="duplicate-password">
    <!-- Multiple hidden fake inputs to trick browser autofill -->
    <input
      type="text"
      autocomplete="username"
      name="fake_username_1"
      style="position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; pointer-events: none;"
      tabindex="-1"
      readonly
    />
    <input
      type="password"
      autocomplete="current-password"
      name="fake_password_1"
      style="position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; pointer-events: none;"
      tabindex="-1"
      readonly
    />
    <input
      type="text"
      autocomplete="username"
      name="fake_username_2"
      style="position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; pointer-events: none;"
      tabindex="-1"
      readonly
    />
    <input
      type="password"
      autocomplete="current-password"
      name="fake_password_2"
      style="position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; pointer-events: none;"
      tabindex="-1"
      readonly
    />
    <div class="fp-form-group">
      <div
        class="fp-input-wrapper"
        :class="{ 'fp-has-value': localNewPassword }"
      >
        <div class="fp-input-container" :class="{ 'fp-input-error': isWeakPassword }">
          <div v-if="localNewPassword" class="fp-label-strip">
            <div class="fp-label-text">
              <span :class="{ 'fp-label-error-text': isWeakPassword }">{{ $t('EMP_UserSetting_EnterNewPassword') }}</span>
              <span class="fp-required" :class="{ 'fp-required-error': isWeakPassword }">*</span>
            </div>
          </div>
          <div class="fp-input-inner">
            <div v-if="!localNewPassword" class="fp-input-placeholder">
              <span>{{ $t('EMP_UserSetting_EnterNewPassword') }}</span><span class="fp-required">*</span>
            </div>
            <input
              ref="newPasswordInput"
              :value="localNewPassword"
              type="text"
              class="fp-form-input"
              :class="{ 
                'fp-input-error': isWeakPassword,
                'fp-password-masked': !showNewPassword
              }"
              autocomplete="off"
              :name="randomInputName1"
              id="new-password-input"
              aria-autocomplete="none"
              spellcheck="false"
              autocorrect="off"
              autocapitalize="off"
              inputmode="text"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              data-bwignore="true"
              data-bwignore-field="true"
              data-chrome-autofill-disabled="true"
              :readonly="isReadonly1"
              @input="handleNewPasswordInput"
              @focus="handleNewPasswordFocus"
              @blur="handleNewPasswordBlur"
              @keydown.enter.prevent
            />
            <button
              type="button"
              class="fp-password-toggle"
              @click="toggleNewPassword"
            >
              <svg v-if="!showNewPassword" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
                <line x1="1" y1="1" x2="23" y2="23" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
              </svg>
              <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
                <circle cx="12" cy="12" r="3" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
              </svg>
            </button>
          </div>
        </div>
        <div v-if="isWeakPassword" class="fp-error-message">
          {{ $t('EMP_UserSetting_PasswordStrengthTip') }}
        </div>
        <div v-if="localNewPassword" class="fp-password-strength-indicator">
          <div class="fp-strength-bar-item">
            <div class="fp-strength-bar-segment" :class="{
              'fp-strength-weak': passwordStrength === 'weak',
              'fp-strength-medium': passwordStrength === 'medium' || passwordStrength === 'strong',
              'fp-strength-strong': passwordStrength === 'strong'
            }"></div>
            <div v-if="passwordStrength === 'weak'" class="fp-strength-label fp-strength-label-weak">{{ $t('lbl_emp2_PasswordWeak') }}</div>
          </div>
          <div class="fp-strength-bar-item">
            <div class="fp-strength-bar-segment" :class="{
              'fp-strength-medium': passwordStrength === 'medium' || passwordStrength === 'strong',
              'fp-strength-strong': passwordStrength === 'strong'
            }"></div>
            <div v-if="passwordStrength === 'medium'" class="fp-strength-label fp-strength-label-medium">{{ $t('lbl_emp2_PasswordMedium') }}</div>
          </div>
          <div class="fp-strength-bar-item">
            <div class="fp-strength-bar-segment" :class="{
              'fp-strength-strong': passwordStrength === 'strong'
            }"></div>
            <div v-if="passwordStrength === 'strong'" class="fp-strength-label fp-strength-label-strong">{{ $t('lbl_emp2_PasswordStrong') }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="fp-form-group">
      <div class="fp-input-wrapper" :class="{ 'fp-has-value': localConfirmPassword }">
        <div class="fp-input-container" :class="{ 'fp-input-error': isConfirmMismatch }">
          <div v-if="localConfirmPassword" class="fp-label-strip">
            <div class="fp-label-text">
              <span :class="{ 'fp-label-error-text': isConfirmMismatch }">{{ $t('EMP_UserSetting_ConfirmNewPassword') }}</span>
              <span class="fp-required" :class="{ 'fp-required-error': isConfirmMismatch }">*</span>
            </div>
          </div>
          <div class="fp-input-inner">
            <div v-if="!localConfirmPassword" class="fp-input-placeholder">
              <span>{{ $t('EMP_UserSetting_ConfirmNewPassword') }}</span><span class="fp-required">*</span>
            </div>
            <input
              ref="confirmPasswordInput"
              :value="localConfirmPassword"
              type="text"
              class="fp-form-input"
              :class="{ 
                'fp-input-error': isConfirmMismatch,
                'fp-password-masked': !showConfirmPassword
              }"
              autocomplete="off"
              :name="randomInputName2"
              id="confirm-password-input"
              aria-autocomplete="none"
              spellcheck="false"
              autocorrect="off"
              autocapitalize="off"
              inputmode="text"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              data-bwignore="true"
              data-bwignore-field="true"
              data-chrome-autofill-disabled="true"
              :readonly="isReadonly2"
              @input="handleConfirmPasswordInput"
              @focus="handleConfirmPasswordFocus"
              @blur="handleConfirmPasswordBlur"
              @keydown.enter.prevent
            />
            <button
              type="button"
              class="fp-password-toggle"
              @click="toggleConfirmPassword"
            >
              <svg v-if="!showConfirmPassword" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
                <line x1="1" y1="1" x2="23" y2="23" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
              </svg>
              <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
                <circle cx="12" cy="12" r="3" stroke="rgba(0,0,0,0.6)" stroke-width="2"/>
              </svg>
            </button>
          </div>
        </div>
        <div v-if="isConfirmMismatch" class="fp-error-message">
          {{ $t('EMP_UserSetting_PasswordsDoNotMatch') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import inputMixin from '../new-login/Common/input-mixin.js';
import { checkPasswordStrength } from '../new-login/Common/utils.js';

export default {
  name: 'DuplicatePassword',
  mixins: [inputMixin],
  emits: [
    'update:newPassword',
    'update:confirmPassword',
    'update:passwordStrength'
  ],
  props: {
    newPassword: {
      type: String,
      default: ''
    },
    confirmPassword: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      localNewPassword: this.newPassword,
      localConfirmPassword: this.confirmPassword,
      passwordStrength: checkPasswordStrength(this.newPassword),
      showNewPassword: false,
      showConfirmPassword: false,
      isReadonly1: true,
      isReadonly2: true,
      randomInputName1: `pwd_${Math.random().toString(36).substring(2, 15)}`,
      randomInputName2: `pwd_${Math.random().toString(36).substring(2, 15)}`
    };
  },
  computed: {
    isWeakPassword() {
      return this.localNewPassword && this.localNewPassword.length < 6;
    },
    isConfirmMismatch() {
      return this.localConfirmPassword && this.localNewPassword !== this.localConfirmPassword;
    }
  },
  mounted() {
    // Reset readonly state when component is mounted to prevent autofill
    this.$nextTick(() => {
      this.isReadonly1 = true;
      this.isReadonly2 = true;
      // Also set readonly attribute directly on DOM elements
      if (this.$refs.newPasswordInput) {
        this.$refs.newPasswordInput.setAttribute('readonly', 'readonly');
      }
      if (this.$refs.confirmPasswordInput) {
        this.$refs.confirmPasswordInput.setAttribute('readonly', 'readonly');
      }
    });
  },
  watch: {
    newPassword(val) {
      this.localNewPassword = val;
      this.passwordStrength = checkPasswordStrength(val);
      // Reset readonly when password is cleared (modal reopened)
      if (!val) {
        this.isReadonly1 = true;
      }
    },
    confirmPassword(val) {
      this.localConfirmPassword = val;
      // Reset readonly when password is cleared (modal reopened)
      if (!val) {
        this.isReadonly2 = true;
      }
    }
  },
  methods: {
    handleNewPasswordInput(event) {
      const value = event.target.value;
      this.localNewPassword = value;
      this.passwordStrength = checkPasswordStrength(value);
      this.$emit('update:newPassword', value);
      this.$emit('update:passwordStrength', this.passwordStrength);
    },
    handleNewPasswordFocus(event) {
      // Remove readonly with delay to prevent autofill
      if (this.isReadonly1) {
        // Use setTimeout to delay removing readonly (prevents autofill)
        setTimeout(() => {
          this.isReadonly1 = false;
          if (event.target) {
            event.target.removeAttribute('readonly');
            // Force remove autocomplete attribute
            event.target.setAttribute('autocomplete', 'off');
            // Try to prevent Chrome autofill by setting a random value temporarily
            const originalValue = event.target.value;
            event.target.value = '';
            setTimeout(() => {
              event.target.value = originalValue;
              event.target.focus();
            }, 50);
          }
        }, 150);
      }
      this.onFocus('clear-error');
    },
    handleNewPasswordBlur() {
      this.onBlur();
    },
    handleConfirmPasswordInput(event) {
      const value = event.target.value;
      this.localConfirmPassword = value;
      this.$emit('update:confirmPassword', value);
    },
    handleConfirmPasswordFocus(event) {
      // Remove readonly with delay to prevent autofill
      if (this.isReadonly2) {
        // Use setTimeout to delay removing readonly (prevents autofill)
        setTimeout(() => {
          this.isReadonly2 = false;
          if (event.target) {
            event.target.removeAttribute('readonly');
            // Force remove autocomplete attribute
            event.target.setAttribute('autocomplete', 'off');
            // Try to prevent Chrome autofill by setting a random value temporarily
            const originalValue = event.target.value;
            event.target.value = '';
            setTimeout(() => {
              event.target.value = originalValue;
              event.target.focus();
            }, 50);
          }
        }, 150);
      }
      this.onFocus('clear-error');
    },
    handleConfirmPasswordBlur() {
      this.onBlur();
    },
    toggleNewPassword() {
      this.showNewPassword = !this.showNewPassword;
    },
    toggleConfirmPassword() {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }
};
</script>

<style scoped>
.duplicate-password {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 24px;
}

.fp-form-group {
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  display: flex;
}

.fp-input-wrapper {
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 3px;
  display: flex;
}

.fp-input-container {
  align-self: stretch;
  position: relative;
  padding-right: 12px;
  border-radius: 4px;
  outline: 1px rgba(0, 0, 0, 0.23) solid;
  outline-offset: -1px;
  background: transparent;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
}

.fp-input-container.fp-input-error {
  outline-color: #D32F2F !important;
}

.fp-label-strip {
  height: 2px;
  padding-left: 4px;
  padding-right: 4px;
  background: white;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  display: inline-flex;
}

.fp-label-text {
  justify-content: center;
  display: inline-block;
}

.fp-label-text span {
  display: inline;
  color: rgba(0, 0, 0, 0.60);
  font-size: 12px;
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  line-height: 12px;
  letter-spacing: 0.15px;
  word-wrap: break-word;
  white-space: nowrap;
}

.fp-label-text span.fp-label-error-text {
  color: #D32F2F;
}

.fp-required {
  color: #CD1515 !important;
  font-size: 14px;
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.15px;
  margin-left: 4px;
}

.fp-label-text .fp-required {
  font-size: 12px;
  line-height: 12px;
  color: #CD1515 !important;
  margin-left: 4px;
}

.fp-required.fp-required-error {
  color: #D32F2F;
}

.fp-input-inner {
  align-self: stretch;
  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 12px;
  padding-right: 12px;
  overflow: hidden;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  display: inline-flex;
  position: relative;
}

.fp-input-wrapper.fp-has-value .fp-input-inner {
  padding-top: 7px;
  padding-bottom: 7px;
  padding-left: 12px;
  padding-right: 12px;
}

.fp-input-placeholder {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  flex: 1 1 0;
  pointer-events: none;
  z-index: 1;
  display: inline-block;
}

.fp-input-placeholder span {
  display: inline;
  color: rgba(0, 0, 0, 0.60);
  font-size: 14px;
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.15px;
  word-wrap: break-word;
  white-space: nowrap;
}

.fp-input-placeholder .fp-required {
  color: #CD1515 !important;
  margin-left: 4px;
}

.fp-form-input {
  flex: 1 1 0;
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  font-size: 14px;
  font-family: 'Roboto', sans-serif;
  color: rgba(0, 0, 0, 0.87);
  line-height: 24px;
  letter-spacing: 0.15px;
  outline: none;
  z-index: 2;
}

/* Use text-security to mask password while using type="text" to avoid autofill */
/* stylelint-disable property-no-unknown */
.fp-form-input.fp-password-masked {
  -webkit-text-security: disc;
  text-security: disc;
  font-family: 'Roboto', sans-serif;
}
/* stylelint-enable property-no-unknown */

.fp-form-input:focus {
  outline: none;
  border: none;
  background: transparent;
}

.fp-password-toggle {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
}

.fp-password-toggle svg {
  width: 24px;
  height: 24px;
}

.fp-password-toggle:hover {
  opacity: 0.7;
}

.fp-error-message {
  align-self: stretch;
  padding-left: 14px;
  padding-right: 14px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  color: #D32F2F;
  font-size: 12px;
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.40px;
  word-wrap: break-word;
}

.fp-password-strength-indicator {
  align-self: stretch;
  justify-content: space-between;
  align-items: flex-start;
  gap: 4px;
  display: flex;
  margin-top: 8px;
  width: 100%;
}

.fp-strength-bar-item {
  flex: 1 1 0;
  min-width: 0;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 8px;
  display: flex;
}

.fp-strength-bar-segment {
  width: 100%;
  height: 4px;
  background: #AAAAAA;
  flex-shrink: 0;
}

.fp-strength-bar-segment.fp-strength-weak {
  background: #DC3545;
}

.fp-strength-bar-segment.fp-strength-medium {
  background: #FF952C;
}

.fp-strength-bar-segment.fp-strength-strong {
  background: #4CAF50;
}

.fp-strength-label {
  align-self: stretch;
  min-height: 19px;
  text-align: center;
  justify-content: center;
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 15.60px;
  word-wrap: break-word;
  word-break: break-word;
}

.fp-strength-label-weak {
  color: #DC3545;
}

.fp-strength-label-medium {
  color: #FF952C;
}

.fp-strength-label-strong {
  color: #4CAF50;
}
</style>

